import React from 'react';
import { Button, Checkbox } from 'components/atoms/index';
import * as styles from './Table.css';
import { Icon } from 'icons';
import { useNavigate } from 'react-router-dom';

interface WordData {
  id: string;
  wordCode: string;
  word: string;
  meaning: string;
  ttsType: string;
  registrant: string;
  createdAt: string;
  uploadStatus: 'completed' | 'failed';
}

interface WordTableProps {
  data: WordData[];
  type: 'list' | 'display' | 'wordBookUpload' | 'wordBookSelect';
  onDelete?: (id: string) => void;
  onEdit?: (id: string) => void;
  showCheckbox?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (id: string, checked: boolean) => void;
  onRowClick?: (id: string) => void;
  headerCheckbox?: {
    checked: boolean;
    indeterminate?: boolean;
    onChange: (checked: boolean) => void;
  };
  noResolver?: (index: number, row: WordData) => number | string;
}

export default function WordTable({ 
  data, 
  type,
  onDelete, 
  onEdit, 
  showCheckbox = false, 
  selectedIds = [], 
  onSelectionChange,
  onRowClick,
  headerCheckbox,
  noResolver
}: WordTableProps) {
  const navigate = useNavigate();

  if (data.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem 1rem',
        color: '#666',
        fontSize: '1rem'
      }}>
        데이터가 없습니다.
      </div>
    );
  }

  const renderUploadStatus = (status: WordData['uploadStatus']) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full">등록완료</span>;
      case 'failed':
        return <span className="px-2 py-1 text-xs text-red-800 bg-red-100 rounded-full">등록 불가</span>;
      default:
        return <span className="px-2 py-1 text-xs text-gray-800 bg-gray-100 rounded-full">-</span>;
    }
  };

  const handleRowClick = (rowId: string, isSelected: boolean) => {
    if (showCheckbox && onSelectionChange) {
      onSelectionChange(rowId, !isSelected);
      return;
    }
    if (onRowClick) {
      onRowClick(rowId);
      return;
    }
    if (type === 'list') {
      navigate(`/academy/word/detail/${rowId}`);
    }
  };

  const mapTtsToKorean = (tts: string) => {
    const [genderRaw, ageRaw, speedRaw, pitchRaw] = (tts || '').split('/').map(s => (s || '').trim());
    const genderText = genderRaw === 'male' ? '남성' : (genderRaw === 'female' ? '여성' : genderRaw);
    const ageText = (
      ageRaw === 'child' ? '아동' :
      ageRaw === 'young' ? '청소년' :
      ageRaw === 'adult' ? '성인' :
      ageRaw === 'elder' ? '노년' : ageRaw
    );
    const speedText = (
      speedRaw === 'slow' ? '느림' :
      speedRaw === 'normal' ? '보통' :
      speedRaw === 'fast' ? '빠름' : speedRaw
    );
    const pitchText = (
      pitchRaw === 'low' ? '낮음' :
      pitchRaw === 'medium' ? '보통' :
      pitchRaw === 'high' ? '높음' : pitchRaw
    );
    return (
      <div className="text-[12px] leading-[1.4] text-gray-600">
        <div>{genderText} / {ageText}</div>
        <div>{speedText} / {pitchText}</div>
      </div>
    );
  };

  return (
    <div className='w-full'>
      {/* 테이블 헤더 */}
      <ul className={styles.academyHeaderList}>
        {showCheckbox && (
          <li className={`${styles.headerItem} max-w-[80px] justify-center`}>
            <Checkbox
              checked={!!headerCheckbox?.checked}
              indeterminate={!!headerCheckbox?.indeterminate}
              onChange={(v) => headerCheckbox?.onChange?.(v)}
            />
          </li>
        )}
        <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
        {type === 'list' && <li className={`${styles.headerItem}`}>단어코드</li>}
        <li className={`${styles.headerItem}`}>단어</li>
        {(type === 'display' || type === 'wordBookUpload' || type === 'wordBookSelect')  && <li className={`${styles.headerItem}`}>해석</li>}
        {type === 'wordBookUpload' && <li className={`${styles.headerItem}`}>단어코드</li>}
        {type === 'wordBookSelect' && <li className={`${styles.headerItem} justify-center`}>TTS 유형</li>}
        {type === 'list' && <li className={`${styles.headerItem} justify-center`}>TTS 유형</li>}
        {type === 'list' && <li className={`${styles.headerItem}`}>등록자</li>}
        {type === 'list' && <li className={`${styles.headerItem}`}>등록일</li>}
        {(type === 'display' ) && <li className={`${styles.headerItem} justify-center`}>업로드 상태</li>}
        {(type === 'display' || type === 'wordBookUpload') && onDelete && <li className={`${styles.headerItem} justify-center`}>작업</li>}
      </ul>

      {/* 테이블 바디 */}
      {data.map((row, index) => {
        const isSelected = selectedIds?.includes(row.id) || false;
        
        return (
          <div 
            key={row.id} 
            className={`${styles.mobileCard} ${(showCheckbox || type === 'list' || onRowClick) ? `${styles.hoverRow} cursor-pointer` : ''} ${isSelected ? styles.selectedRow : ''}`}
            onClick={() => handleRowClick(row.id, isSelected)}
          >
            <ul className={styles.bodyList}>
              {showCheckbox && (
                <li className={`${styles.bodyItem} max-w-[80px] hidden lg:flex justify-center`}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(checked) => onSelectionChange?.(row.id, checked)}
                  />
                </li>
              )}
              <li className={`${styles.bodyItem} max-w-[80px] hidden lg:flex justify-center`}>
                {noResolver
                  ? noResolver(index, row)
                  : (data.length > 0 ? data.length - index : 0)}
              </li>
              {type === 'list' && (
                <li className={`${styles.bodyItem} text-sm text-gray-600`}>{row.wordCode}</li>
              )}
              <li className={`${styles.bodyItem} lg:flex-1 font-medium`}>{row.word}</li>
              {(type === 'display' || type === 'wordBookUpload' || type === 'wordBookSelect') && (
                <li className={`${styles.bodyItem} lg:flex-1 text-sm text-gray-600`}>{row.meaning}</li>
              )}
              {type === 'wordBookUpload' && (
                <li className={`${styles.bodyItem} text-sm text-gray-500`}>{row.wordCode || '-'}</li>
              )}
              {type === 'wordBookSelect' && (
                <li className={`${styles.bodyItem} text-sm text-gray-500 lg:flex justify-center`}>
                  {mapTtsToKorean(row.ttsType)}
                </li>
              )}
              {type === 'list' && (
                <li className={`${styles.bodyItem} text-sm text-gray-500 lg:flex justify-center`}>
                  {row.ttsType}
                </li>
              )}
              {type === 'list' && (
                <li className={`${styles.bodyItem} text-sm text-gray-500 lg:static`}>
                  {row.registrant}
                </li>
              )}
              {type === 'list' && (
                <li className={`${styles.bodyItem} text-sm text-gray-500 lg:static`}>
                  {row.createdAt}
                </li>
              )}
              {type === 'display' && (
                <li className={`${styles.bodyItem} text-sm text-gray-500 lg:flex justify-center`}>
                  {renderUploadStatus(row.uploadStatus)}
                </li>
              )}
              {(type === 'display' || type === 'wordBookUpload') && onDelete && (
                <li className={`${styles.bodyItem} justify-center`}>
                  <div className={styles.actionButton}>
                    <Button
                      intent="danger"
                      size="small"
                      outline
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(row.id);
                      }}
                    >
                      삭제
                    </Button>
                  </div>
                </li>
              )}
            </ul>
          </div>
        );
      })}
    </div>
  );
} 